import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Modal,
  Form,
  message,
  Drawer,
} from 'antd';
import {
  PlusOutlined,
  BarChartOutlined,
  ToolOutlined,
  SaveOutlined,
  UploadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';

// Import shared components and utilities
import LibraryTable from '../components/shared/LibraryTable';
import FilterPanel from '../components/shared/FilterPanel';
import StatsCard from '../components/shared/StatsCard';
import { Equipment, EquipmentFilters } from '../types';
import { filterEquipment, debounce, getUniqueValues, getFilterOptions } from '../utils/filterUtils';
import { 
  EQUIPMENT_CATEGORIES, 
  EQUIPMENT_STATUS, 
  EQUIPMENT_CONDITION,
  MAINTENANCE_SCHEDULES,
  UTILIZATION_RANGES,
  COST_RANGES,
  AGE_RANGES,
  SEARCH_DEBOUNCE_MS 
} from '../constants';

// Import feature-specific components (to be created)
import EquipmentForm from './components/EquipmentForm';
import EquipmentDetailsModal from './components/EquipmentDetailsModal';
import EquipmentStatsDrawer from './components/EquipmentStatsDrawer';
import { getEquipmentTableColumns } from './utils/equipmentTableConfig';
import { mockEquipmentData } from './data/mockData';
import { apiService } from '../../../services/api';

const { Title } = Typography;

const EquipmentLibrary: React.FC = () => {
  // State management
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [filteredEquipment, setFilteredEquipment] = useState<Equipment[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isStatsDrawerVisible, setIsStatsDrawerVisible] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  const [selectedEquipmentForDetails, setSelectedEquipmentForDetails] = useState<Equipment | null>(null);
  
  // Filter states
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState<EquipmentFilters>({
    searchText: '',
    categories: [],
    statuses: [],
    conditions: [],
    brands: [],
    locations: [],
    maintenanceSchedules: [],
    utilizationRanges: [],
    costRanges: [],
    ageRanges: [],
    maintenanceDue: false,
    warrantyExpiring: false,
  });

  const [form] = Form.useForm();

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        setInitialLoading(true);
        
        const response = await apiService.getEquipment({ limit: 100 });

        if (response.success && response.data) {
          // Handle different response formats from backend
          const equipmentData = Array.isArray(response.data)
            ? response.data
            : response.data.data || [];
          setEquipment(equipmentData);
        } else {
          console.warn('Failed to load equipment, using mock data');
          setEquipment(mockEquipmentData);
        }

      } catch (error) {
        console.error('Error loading equipment:', error);
        message.error('Failed to load equipment. Using cached data.');
        // Fallback to mock data on error
        setEquipment(mockEquipmentData);
      } finally {
        setInitialLoading(false);
      }
    };

    loadData();
  }, []);

  // Apply filters with debouncing
  const debouncedFilter = useCallback(
    debounce(() => {
      const filtered = filterEquipment(equipment, filters);
      setFilteredEquipment(filtered);
    }, SEARCH_DEBOUNCE_MS),
    [equipment, filters]
  );

  useEffect(() => {
    debouncedFilter();
  }, [debouncedFilter]);

  // Filter handlers
  const handleFilterChange = (key: keyof EquipmentFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      searchText: '',
      categories: [],
      statuses: [],
      conditions: [],
      brands: [],
      locations: [],
      maintenanceSchedules: [],
      utilizationRanges: [],
      costRanges: [],
      ageRanges: [],
      maintenanceDue: false,
      warrantyExpiring: false,
    });
  };

  const getTotalFiltersApplied = () => {
    let count = 0;
    if (filters.searchText) count++;
    count += filters.categories.length;
    count += filters.statuses.length;
    count += filters.conditions.length;
    count += filters.brands.length;
    count += filters.locations.length;
    count += filters.maintenanceSchedules.length;
    count += filters.utilizationRanges.length;
    count += filters.costRanges.length;
    count += filters.ageRanges.length;
    if (filters.maintenanceDue) count++;
    if (filters.warrantyExpiring) count++;
    return count;
  };

  // Table handlers
  const handleAdd = () => {
    setEditingEquipment(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: Equipment) => {
    setEditingEquipment(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = async (record: Equipment) => {
    try {
      setLoading(true);
      const response = await apiService.deleteEquipment(record.id);
      
      if (response.success) {
        setEquipment(prev => prev.filter(item => item.id !== record.id));
        message.success('Equipment deleted successfully');
      } else {
        message.error('Failed to delete equipment');
      }
    } catch (error) {
      console.error('Error deleting equipment:', error);
      message.error('Failed to delete equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (record: Equipment) => {
    setSelectedEquipmentForDetails(record);
    setIsDetailModalVisible(true);
  };

  // Helper function to map frontend categories to backend EquipmentType enum
  const mapCategoryToType = (category: string): string => {
    const categoryMap: Record<string, string> = {
      'Cardio': 'CARDIO_EQUIPMENT',
      'Strength': 'BARBELL',
      'Functional': 'KETTLEBELL', 
      'Accessories': 'RESISTANCE_BANDS',
      'Technology': 'OTHER',
    };
    return categoryMap[category] || 'OTHER';
  };

  const handleSave = async (values: any) => {
    try {
      setLoading(true);
      
      // Transform frontend data to match backend schema
      const transformedValues = {
        name: values.name,
        // Map frontend categories to backend EquipmentType enum
        type: mapCategoryToType(values.category),
        description: values.description || values.brand || values.model || '',
        imageUrl: values.imageUrl || '',
      };
      
      // Validation
      if (!transformedValues.name || transformedValues.name.trim().length < 2) {
        message.error('Equipment name must be at least 2 characters');
        return;
      }
      
      console.log('Transformed equipment data:', transformedValues);
      
      if (editingEquipment) {
        // Update existing equipment
        const response = await apiService.updateEquipment(editingEquipment.id, transformedValues);
        
        if (response.success && response.data) {
          setEquipment(prev => prev.map(item => 
            item.id === editingEquipment.id ? response.data! : item
          ));
          message.success('Equipment updated successfully');
        } else {
          message.error('Failed to update equipment');
          return;
        }
      } else {
        // Add new equipment
        const response = await apiService.createEquipment(transformedValues);
        
        if (response.success && response.data) {
          setEquipment(prev => [...prev, response.data!]);
          message.success('Equipment added successfully');
        } else {
          message.error('Failed to create equipment');
          return;
        }
      }
      
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving equipment:', error);
      message.error('Failed to save equipment');
    } finally {
      setLoading(false);
    }
  };

  // Get filter options from current data
  const getFilterGroups = () => [
    {
      key: 'categories',
      label: 'Category',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(EQUIPMENT_CATEGORIES)),
      value: filters.categories,
      onChange: (value: string[]) => handleFilterChange('categories', value),
      placeholder: 'Select categories...',
      allowClear: true,
      showCount: true,
    },
    {
      key: 'statuses',
      label: 'Status',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(EQUIPMENT_STATUS)),
      value: filters.statuses,
      onChange: (value: string[]) => handleFilterChange('statuses', value),
      placeholder: 'Select statuses...',
      allowClear: true,
    },
    {
      key: 'conditions',
      label: 'Condition',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(EQUIPMENT_CONDITION)),
      value: filters.conditions,
      onChange: (value: string[]) => handleFilterChange('conditions', value),
      placeholder: 'Select conditions...',
      allowClear: true,
    },
    {
      key: 'brands',
      label: 'Brand',
      type: 'multiSelect' as const,
      options: getFilterOptions(getUniqueValues(equipment, item => item.brand)),
      value: filters.brands,
      onChange: (value: string[]) => handleFilterChange('brands', value),
      placeholder: 'Select brands...',
      allowClear: true,
      showCount: true,
    },
    {
      key: 'locations',
      label: 'Location',
      type: 'multiSelect' as const,
      options: getFilterOptions(getUniqueValues(equipment, item => item.location)),
      value: filters.locations,
      onChange: (value: string[]) => handleFilterChange('locations', value),
      placeholder: 'Select locations...',
      allowClear: true,
      showCount: true,
    },
    {
      key: 'maintenanceSchedules',
      label: 'Maintenance Schedule',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(MAINTENANCE_SCHEDULES)),
      value: filters.maintenanceSchedules,
      onChange: (value: string[]) => handleFilterChange('maintenanceSchedules', value),
      placeholder: 'Select schedules...',
      allowClear: true,
    },
    {
      key: 'utilizationRanges',
      label: 'Utilization',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(UTILIZATION_RANGES).map(r => r.label)),
      value: filters.utilizationRanges,
      onChange: (value: string[]) => handleFilterChange('utilizationRanges', value),
      placeholder: 'Select utilization ranges...',
      allowClear: true,
    },
    {
      key: 'costRanges',
      label: 'Cost Range',
      type: 'multiSelect' as const,
      options: getFilterOptions(Object.values(COST_RANGES).map(r => r.label)),
      value: filters.costRanges,
      onChange: (value: string[]) => handleFilterChange('costRanges', value),
      placeholder: 'Select cost ranges...',
      allowClear: true,
    },
    {
      key: 'maintenanceDue',
      label: 'Maintenance Due',
      type: 'checkbox' as const,
      value: filters.maintenanceDue,
      onChange: (value: boolean) => handleFilterChange('maintenanceDue', value),
    },
    {
      key: 'warrantyExpiring',
      label: 'Warranty Expiring Soon',
      type: 'checkbox' as const,
      value: filters.warrantyExpiring,
      onChange: (value: boolean) => handleFilterChange('warrantyExpiring', value),
    },
  ];

  return (
    <div>
      <Row gutter={[24, 24]}>
        {/* Header */}
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} style={{ margin: 0 }}>
                  <ToolOutlined /> Equipment Library
                </Title>
              </Col>
              <Col>
                <Space>
                  <Button 
                    icon={<BarChartOutlined />} 
                    onClick={() => setIsStatsDrawerVisible(true)}
                  >
                    Statistics
                  </Button>
                  <Button icon={<UploadOutlined />}>Import</Button>
                  <Button icon={<DownloadOutlined />}>Export</Button>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />} 
                    onClick={handleAdd}
                    loading={loading}
                  >
                    Add Equipment
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Filters */}
        <Col span={24}>
          <FilterPanel
            searchValue={filters.searchText}
            onSearchChange={(value) => handleFilterChange('searchText', value)}
            filterGroups={getFilterGroups()}
            onClearAll={handleClearAllFilters}
            expanded={filtersExpanded}
            onExpandedChange={setFiltersExpanded}
            totalFiltersApplied={getTotalFiltersApplied()}
          />
        </Col>

        {/* Table */}
        <Col span={24}>
          <Card>
            <LibraryTable
              data={filteredEquipment}
              columns={getEquipmentTableColumns()}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleView}
              selectedRowKeys={selectedRowKeys}
              onSelectionChange={(keys, rows) => setSelectedRowKeys(keys)}
              loading={initialLoading || loading}
              pagination={{
                total: filteredEquipment.length,
                showTotal: (total, range) => 
                  `Showing ${range[0]}-${range[1]} of ${total} equipment items`,
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Equipment Form Modal */}
      <Modal
        title={editingEquipment ? 'Edit Equipment' : 'Add New Equipment'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        destroyOnHidden
      >
        <EquipmentForm
          form={form}
          equipment={editingEquipment}
          onSave={handleSave}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>

      {/* Equipment Details Modal */}
      <EquipmentDetailsModal
        visible={isDetailModalVisible}
        equipment={selectedEquipmentForDetails}
        onClose={() => setIsDetailModalVisible(false)}
      />

      {/* Statistics Drawer */}
      <EquipmentStatsDrawer
        visible={isStatsDrawerVisible}
        equipment={equipment}
        onClose={() => setIsStatsDrawerVisible(false)}
      />
    </div>
  );
};

export default EquipmentLibrary; 